import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Send, CheckCircle } from 'lucide-react';

const JoinNextChapter = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Create mailto link with form data
    const subject = encodeURIComponent(`IPMI 50th Anniversary - Inquiry from ${formData.name}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\n` +
      `Email: ${formData.email}\n` +
      `Organization: ${formData.organization}\n\n` +
      `Message:\n${formData.message}`
    );
    
    // Open mail client
    window.location.href = `mailto:mail@ipmi.org?subject=${subject}&body=${body}`;
    
    // Show success state after a brief delay
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 500);
  };

  return (
    <section className="join-next-chapter-section" ref={sectionRef}>
      <div className="section-container">
        {/* Contact Form - Centered */}
        <motion.div
          className="join-form-centered"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="contact-form" data-testid="join-contact-form">
              <h3 className="form-heading">Get in Touch</h3>
              <p className="form-subheading">Our team will follow up personally</p>
              
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    data-testid="input-name"
                  />
                </div>
                
                <div className="form-group">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    data-testid="input-email"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <input
                  type="text"
                  name="organization"
                  placeholder="Organization (Optional)"
                  value={formData.organization}
                  onChange={handleChange}
                  className="form-input"
                  data-testid="input-organization"
                />
              </div>
              
              <div className="form-group">
                <textarea
                  name="message"
                  placeholder="How can we help? Tell us about your interest in IPMI..."
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="form-textarea"
                  data-testid="input-message"
                />
              </div>
              
              <button 
                type="submit" 
                className="form-submit-btn"
                disabled={isSubmitting}
                data-testid="submit-btn"
              >
                {isSubmitting ? (
                  <span>Sending...</span>
                ) : (
                  <>
                    <span>Send Message</span>
                    <Send size={18} />
                  </>
                )}
              </button>
            </form>
          ) : (
            <motion.div 
              className="form-success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              data-testid="form-success"
            >
              <CheckCircle size={48} className="success-icon" />
              <h3>Thank You!</h3>
              <p>Your email client should have opened with your message. If not, you can reach us directly at <a href="mailto:mail@ipmi.org">mail@ipmi.org</a></p>
              <button 
                onClick={() => {
                  setIsSubmitted(false);
                  setFormData({ name: '', email: '', organization: '', message: '' });
                }}
                className="reset-form-btn"
              >
                Send Another Message
              </button>
            </motion.div>
          )}
        </motion.div>

        {/* Trust indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="trust-indicators"
        >
          <p className="thank-you-text">Thank you to every member, volunteer, and partner for 50 years of trust and leadership.</p>
        </motion.div>
      </div>
    </section>
  );
};

export default JoinNextChapter;
